import { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Modal,
  ModalHeader,
} from "reactstrap";
import ModalBody from "reactstrap/lib/ModalBody";
import TableV3 from "src/components/atoms/TableV3/TableV3";
import { expandJSON } from "src/helpers/helper";
import useLazyFetch from "src/hooks/useLazyFetch";
import AddEditBridge from "./contents/AddEditBridge";
import DeleteBridge from "./contents/DeleteBridge";
import DeviceList from "./contents/Device/DeviceList";

const LockBridges = () => {
  const [addModal, setAddModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{
    id: string | number;
    name: string;
    device_id: string;
  } | null>(null);
  const [editModal, setEditModal] = useState<{
    id: string | number;
    name: string;
    device_id: string;
  } | null>(null);
  const [deviceModal, setDeviceModal] = useState<{
    id: string | number;
    name: string;
    device_id: string;
  } | null>(null);
  const [onUserList, { loading, data }] = useLazyFetch({
    url: "https://api.automa.id/api/iot/lock/bridges/list",
    method: "POST",
  });

  const [oneTime, setOneTime] = useState(true);
  const [loadContent, setLoadContent] = useState(false);
  const [sort, setSort] = useState<
    {
      by: string;
      order: "desc" | "asc";
    }[]
  >([]);
  const [filter, setFilter] = useState([]);
  const [page, setPage] = useState({
    of: 1,
    size: 20,
  });

  const convertToQuery = ({
    page,
    sort,
    filter,
    show = [],
  }: {
    page: { of: number; size: number };
    sort: { by: string; order: "asc" | "desc" }[];
    filter: any;
    show?: string[];
  }) => {
    let newFilter = {};
    Object.keys(filter).forEach((item) => {
      newFilter = { ...newFilter, [item]: encodeURI(`%${filter[item]}%`) };
    });

    const query = expandJSON({ page, sort, filter: newFilter, show })
      .map((item) => `${item.label}=${item.value}`)
      .join("&");

    return "?" + query;
  };

  useEffect(() => {
    if (loadContent) {
      setLoadContent(false);
      onUserList({
        path: convertToQuery({
          page: page,
          sort:
            sort.length === 0
              ? [
                  {
                    by: "created_on",
                    order: "desc",
                  },
                ]
              : sort,
          filter: filter,
        }),
      });
    }
  }, [filter, page, sort, loadContent, onUserList]);

  useEffect(() => {
    if (oneTime) {
      setOneTime(false);
      setLoadContent(true);
    }
  }, [oneTime]);

  return (
    <>
      <Card>
        <CardHeader>Locks</CardHeader>
        <CardBody>
          <Button
            color="success"
            type="button"
            onClick={() => setAddModal(true)}
          >
            Add Bridge
          </Button>
          <TableV3
            sort={sort}
            onSort={(s) => {
              setSort([...s]);
              setPage({ ...page, of: 1 });
              setLoadContent(true);
            }}
            page={page}
            totalPage={
              Math.ceil(((!!data ? data.result.total : 1) ?? 1) / page.size) ??
              1
            }
            onPage={(page) => {
              setPage(page);
              setLoadContent(true);
            }}
            filter={filter}
            onFilter={(f) => {
              setFilter(f);
              setPage({ ...page, of: 1 });
              setLoadContent(true);
            }}
            data={!!data ? data.result.data : []}
            columns={[
              {
                name: "Name",
                field: "name",
                sortable: true,
                searchable: true,
              },
              {
                name: "Device ID",
                field: "device_id",
                sortable: true,
                searchable: true,
              },
              {
                name: "Created On",
                field: "created_on",
                sortable: true,
                searchable: true,
              },
              {
                name: "Modified On",
                field: "modified_on",
                sortable: true,
                searchable: true,
              },
              {
                name: "Action",
                field: "modified_on",
                render: (cell, row) => {
                  return (
                    <>
                      <Button
                        color="primary"
                        type="button"
                        onClick={() => setDeviceModal(row)}
                      >
                        Devices
                      </Button>
                      <Button
                        color="warning"
                        type="button"
                        onClick={() => setEditModal(row)}
                      >
                        Edit
                      </Button>
                      <Button
                        color="danger"
                        type="button"
                        onClick={() => setDeleteModal(row)}
                      >
                        Delete
                      </Button>
                    </>
                  );
                },
              },
            ]}
            loading={loading}
          />
        </CardBody>
      </Card>

      <Modal isOpen={addModal} toggle={() => setAddModal(!addModal)}>
        <ModalHeader>Add Bridge</ModalHeader>
        <ModalBody>
          {!!addModal ? (
            <AddEditBridge
              onReload={() => setLoadContent(true)}
              onClose={() => setAddModal(false)}
            />
          ) : null}
        </ModalBody>
      </Modal>

      <Modal isOpen={!!editModal} toggle={() => setEditModal(null)}>
        <ModalHeader>
          Edit Bridge {!!editModal ? editModal.name : null}
        </ModalHeader>
        <ModalBody>
          {!!editModal ? (
            <AddEditBridge
              isEdit
              initialValues={editModal}
              onReload={() => setLoadContent(true)}
              onClose={() => setEditModal(null)}
            />
          ) : null}
        </ModalBody>
      </Modal>
      <Modal isOpen={!!deleteModal} toggle={() => setDeleteModal(null)}>
        <ModalHeader>
          Delete {!!deleteModal ? deleteModal.name : null}
        </ModalHeader>
        <ModalBody>
          {!!deleteModal ? (
            <DeleteBridge
              initialValues={deleteModal}
              onReload={() => setLoadContent(true)}
              onClose={() => setDeleteModal(null)}
            />
          ) : null}
        </ModalBody>
      </Modal>
      <Modal isOpen={!!deviceModal} toggle={() => setDeviceModal(null)}>
        <ModalHeader>
          Bridge {!!deviceModal ? deviceModal.name : null}
        </ModalHeader>
        <ModalBody>
          {!!deviceModal ? (
            <DeviceList
              initialValues={deviceModal}
              onClose={() => setDeviceModal(null)}
            />
          ) : null}
        </ModalBody>
      </Modal>
    </>
  );
};

export default LockBridges;
